import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import styled, { StyledComponentPropsWithRef } from 'styled-components';
import { fetchCampaigns } from '~/javascripts/admin/actions/campaign';
import { formatDate, toQueryParams } from '~/javascripts/admin/utils';
import Link from '../../../atoms/Link';
import Table from '../../../atoms/Table';
import ApiResults from '../../../molecules/ApiResults';
import Pager from '../../../shared/Pager';
import PriorityInput from '../PriorityInput';

type Message = StyledComponentPropsWithRef<typeof ApiResults>['messages'];

type Props = {
  privateMoneyId: string;
  isOngoing?: boolean;
  setApiResult: Dispatch<SetStateAction<Message | undefined>>;
};

type Campaigns = Awaited<ReturnType<typeof fetchCampaigns>>;

const DATE_FORMAT = 'YYYY/MM/DD HH:mm';

const CampaignsTable = ({ privateMoneyId, isOngoing = false, setApiResult }: Props) => {
  const [campaigns, setCampaigns] = useState<Campaigns>();

  const getCampaigns = useCallback(
    (page = 1) => {
      if (privateMoneyId) {
        fetchCampaigns(
          toQueryParams({
            private_money_id: privateMoneyId,
            is_ongoing: isOngoing,
            page,
          })
        ).then((res) => setCampaigns(res));
      }
    },
    [isOngoing, privateMoneyId]
  );

  const onChangePage = (page: number) => {
    getCampaigns(page);
  };

  const updateCampaignPriority = (priority: number, index: number) => {
    if (!campaigns) return;

    const newCampaigns = [...campaigns.rows];
    newCampaigns[index] = { ...newCampaigns[index], priority };

    setCampaigns({ ...campaigns, rows: newCampaigns });
  };

  useEffect(() => {
    getCampaigns();
  }, [privateMoneyId, getCampaigns]);

  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.Cell size="1.5">開始日時</Table.Cell>
          <Table.Cell size="1.5">終了日時</Table.Cell>
          <Table.Cell size="2.5">キャンペーン名</Table.Cell>
          <Table.Cell size="4.5">説明</Table.Cell>
          <Table.Cell size="1">優先度</Table.Cell>
          <Table.Cell size="1">重ね掛け</Table.Cell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {campaigns?.rows?.map((campaign, index) => (
          <Table.Row
            key={campaign.id}
            style={{ background: campaign.is_disabled ? '#ddd' : 'unset' }}
          >
            <Table.Cell>{formatDate(campaign.starts_at, DATE_FORMAT)}</Table.Cell>
            <Table.Cell>{formatDate(campaign.ends_at, DATE_FORMAT)}</Table.Cell>
            <Table.Cell wordBreak="break-word">
              <Link to={`/promotion/campaigns/${campaign.id}`}>{campaign.name}</Link>
            </Table.Cell>
            <Table.Cell wordBreak="break-word">{campaign.description}</Table.Cell>
            <Table.Cell>
              <PriorityInput
                campaign={campaign}
                setApiResult={setApiResult}
                onUpdatePriority={(priority) => updateCampaignPriority(priority, index)}
              />
            </Table.Cell>
            <Table.Cell wordBreak="keep-all">{campaign.is_exclusive ? '無効' : '有効'}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
      <Table.Footer>
        <Table.Row noBorder>
          <Table.Cell colSpan={6} align="center" styles={{ paddingTop: '16px' }}>
            <Pager pagination={campaigns?.pagination} onChangePage={onChangePage} />
          </Table.Cell>
        </Table.Row>
      </Table.Footer>
    </Table>
  );
};

export default styled(CampaignsTable)``;
